import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, generateWeddingId, auth } from '@/lib/firebase';
import { collection, doc, getDoc, getDocs, query, setDoc, serverTimestamp, where } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Heart, ArrowRight, Sparkles, Camera, Smartphone, Tablet, LayoutGrid, Laptop, Monitor,
  Check, Mail, Loader2, PlayCircle, X, Star, QrCode, Users, CalendarClock,
  Wallet, Languages, Gift, ScrollText, ShieldCheck, Music,
} from 'lucide-react';
import { toast } from 'sonner';
import { AgreeNotice } from '@/components/LegalNotice';
import { resolveOrCreateWedding, describeClaimError } from '@/lib/claimWedding';

/* Exported so the honesty tests can assert on every answer. Radix drops
 * collapsed content from the DOM, so scanning the rendered page alone would
 * silently stop covering five of these six. */
export const FAQ_ITEMS = [
  {
    q: 'Do I need to create an account?',
    a: 'Yes, but only you. A one-tap email link or Google, no password to remember. Guests never sign in at all.',
  },
  {
    q: 'Do my guests have to install anything?',
    a: 'No. Invite, RSVP, check-in, quiz and photos all run in the browser from a WhatsApp link. Coordinators can add Live Mode to a home screen so it opens in one tap, if they want.',
  },
  {
    q: 'What happens if the venue Wi-Fi drops?',
    a: 'Check-ins during a brief drop queue on the device and send once you are back, so the door keeps moving. A fully offline day we will not promise yet — the first guest-list load, photo uploads and cross-device sync still need a connection.',
  },
  {
    q: 'Can my coordinator or helpers get in?',
    a: 'Invite them by email as an Admin or a Viewer. Change a role or revoke access at any time; there is always at least one owner.',
  },
  {
    q: 'Can I bring in a guest list I already have?',
    a: 'Yes — import a CSV, with a review screen showing exactly what will change before anything is written. Existing answers are never overwritten.',
  },
  {
    q: 'What does it cost?',
    a: 'Nothing today — payments are not switched on, so every feature is unlocked with no guest cap. The plan is S$59 one-time per wedding, never a subscription, and we will email well before that starts.',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PRO_PRICE_SGD = 59;

/* ── Scroll fade-in hook ── */
const useFadeIn = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
};

const FadeSection = ({ children, className = '', delay = 0 }) => {
  const [ref, visible] = useFadeIn();
  return (
    <div ref={ref}
      className={`transition-[opacity,transform] duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

/* ── Section eyebrow ── */


/* ── Hero product shots ──
 * Captured from the running demo by `scripts/capture-hero.mjs`, so the hero
 * shows the product rather than a drawing of it. The hand-built `HeroPreview`
 * below stays as a fallback and renders whenever the images are absent — a
 * fresh clone has none until someone runs the script, and shipping a broken
 * <img> on the hero would be worse than shipping the mock.
 *
 * Layout follows Framer's: two overlapping frames bleeding off the right edge,
 * so the product reads as bigger than the viewport rather than as a stamp
 * floating in whitespace. The desktop frame is the couple's console, the phone
 * over it is what their guest sees — the two halves of "one guest list".
 */
const HeroShots = ({ onUnavailable }) => (
  <div className="relative w-full">
    {/* Console — deliberately cropped by the container so it continues past
        the fold. `-mr-` pulls it beyond the grid column. */}
    <div className="relative rounded-2xl border border-gold/20 bg-white shadow-[0_24px_70px_rgb(26,42,68,0.14)] overflow-hidden lg:-mr-24 xl:-mr-40">
      {/* Browser chrome: cheap, and it frames a bare screenshot as software. */}
      <div className="flex items-center gap-1.5 px-3.5 py-2.5 border-b border-gold/12 bg-ivory/70">
        <span className="w-2 h-2 rounded-full bg-charcoal/15" />
        <span className="w-2 h-2 rounded-full bg-charcoal/15" />
        <span className="w-2 h-2 rounded-full bg-charcoal/15" />
      </div>
      <img
        src="/hero/console.webp"
        srcSet="/hero/console@900.webp 900w, /hero/console.webp 1340w"
        sizes="(max-width: 1024px) 100vw, 60vw"
        width={1340}
        height={720}
        alt="The Guest Who console showing a wedding's guest list, with invited, confirmed and pending counts"
        className="block w-full h-auto"
        loading="eager"
        fetchpriority="high"
        onError={onUnavailable}
      />
    </div>

    {/* Guest's phone, overlapping the console's lower-left corner. */}
    <div className="absolute -bottom-8 -left-2 sm:left-2 lg:-left-10 w-[132px] sm:w-[156px] rounded-[1.5rem] border border-gold/25 bg-white shadow-[0_18px_50px_rgb(26,42,68,0.18)] overflow-hidden">
      <img
        src="/hero/invite.webp"
        width={402}
        height={860}
        alt="A guest opening the wedding invite on their phone"
        className="block w-full h-auto"
        loading="eager"
        onError={onUnavailable}
      />
    </div>
  </div>
);

/* ── Station previews ──
 * One frame the tabs swap, rather than four cards each explaining a page in a
 * paragraph. The reader can open any of them in the live demo, so these only
 * have to create enough recognition to make that click worth it.
 *
 * Hand-built rather than screenshots: no image assets to keep in sync with a
 * UI that changes, and these stay crisp at any size.
 */
const Chip = ({ children, tone = 'plain' }) => {
  const tones = {
    plain: 'bg-navy/5 text-navy/70 border-navy/10',
    good: 'bg-success/10 text-success border-success/20',
    gold: 'bg-gold/10 text-gold-dark border-gold/40 font-bold tracking-wider uppercase',
  };
  return (
    <span className={`rounded-full border px-2 py-0.5 font-body text-[10px] ${tones[tone]}`}>
      {children}
    </span>
  );
};

const StationCheckin = () => (
  <div className="mx-auto w-[210px] rounded-[1.75rem] bg-white border border-gold/25 shadow-[0_16px_44px_rgb(26,42,68,0.10)] p-2.5">
    <div className="rounded-[1.35rem] bg-ivory p-3.5 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="font-body text-[9px] uppercase tracking-[0.18em] text-gold-dark">Check-in</span>
        <QrCode className="w-3.5 h-3.5 text-gold-dark" strokeWidth={1.5} />
      </div>
      <div className="rounded-xl bg-white border border-gold/15 p-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center font-body text-[10px] font-semibold text-gold-dark">MT</div>
          <div className="min-w-0">
            <p className="font-body text-[11px] font-semibold text-navy truncate">Marcus Teo</p>
            <p className="font-body text-[9px] text-charcoal/50">Groom&rsquo;s side &middot; +1</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Chip tone="good">Arrived</Chip><Chip tone="gold">VIP</Chip><Chip>No beef</Chip>
        </div>
      </div>
      <div className="rounded-xl bg-navy p-2.5 text-center">
        <p className="font-body text-[9px] uppercase tracking-[0.16em] text-gold">Your table</p>
        <p className="font-heading text-2xl text-white leading-tight">8</p>
      </div>
    </div>
  </div>
);

/* Reception is the bridal party's day-of operations console, not check-in on a
 * bigger screen: ReceptionPage subscribes to tasks, vendors, timeline and
 * inventory alongside guests, and its Control tab drives the ballroom screen.
 * The old mock here showed a guest search list, which undersold it exactly the
 * way the old card copy did. */
const StationReception = () => (
  <div className="mx-auto w-full max-w-sm rounded-2xl bg-white border border-gold/20 shadow-[0_16px_44px_rgb(26,42,68,0.08)] p-4 space-y-3">
    <div className="rounded-xl bg-navy px-3 py-2.5">
      <p className="font-body text-[9px] uppercase tracking-[0.16em] text-gold">Now &middot; 20:00</p>
      <p className="font-body text-sm text-white font-semibold mt-0.5">Yum Seng</p>
      <p className="font-body text-[10px] text-white/50">Next 20:30 &middot; Cake cutting</p>
    </div>

    <div className="space-y-1.5">
      {[
        { t: 'Brief emcee on running order', who: 'Marcus', done: true },
        { t: 'Hand red packets to parents', who: 'Jun Wei', done: true },
        { t: 'Collect ang bao box', who: 'Best Man', done: false },
      ].map((task) => (
        <div key={task.t} className="flex items-center gap-2 rounded-lg border border-gold/12 bg-ivory/60 px-2.5 py-1.5">
          <span className={`w-3.5 h-3.5 rounded flex-shrink-0 border flex items-center justify-center ${task.done ? 'bg-success/20 border-success/40' : 'border-charcoal/20'}`}>
            {task.done && <Check className="w-2.5 h-2.5 text-success" strokeWidth={3} />}
          </span>
          <p className={`font-body text-[11px] flex-1 min-w-0 truncate ${task.done ? 'text-charcoal/40 line-through' : 'text-navy'}`}>{task.t}</p>
          <span className="font-body text-[9px] text-charcoal/40 flex-shrink-0">{task.who}</span>
        </div>
      ))}
    </div>

    <div className="flex items-center justify-between pt-1 border-t border-gold/12">
      <p className="font-body text-[10px] text-charcoal/45">Vendors on site</p>
      <div className="flex items-center gap-1">
        <Chip tone="good">Florist</Chip><Chip tone="good">AV</Chip><Chip>Emcee 17:30</Chip>
      </div>
    </div>
  </div>
);

const StationBallroom = () => (
  <div className="mx-auto w-full max-w-sm rounded-2xl bg-navy border border-navy shadow-[0_16px_44px_rgb(26,42,68,0.18)] p-4 space-y-3">
    <div className="grid grid-cols-3 gap-1.5">
      {['bg-gold/30', 'bg-dusty-rose/40', 'bg-gold/20', 'bg-ivory/25', 'bg-gold/35', 'bg-dusty-rose/25'].map((c, i) => (
        <div key={i} className={`aspect-square rounded-lg ${c}`} />
      ))}
    </div>
    <div className="flex items-baseline justify-between">
      <div>
        <p className="font-body text-[9px] uppercase tracking-[0.16em] text-gold">Arrived</p>
        <p className="font-heading text-2xl text-white leading-tight">184 <span className="text-white/40 text-base">/ 240</span></p>
      </div>
      <p className="font-body text-[10px] text-white/50">21 of 24 tables</p>
    </div>
    <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
      <div className="h-full w-[77%] rounded-full bg-gold" />
    </div>
  </div>
);

const StationAdmin = () => (
  <div className="mx-auto w-full max-w-sm rounded-2xl bg-white border border-gold/20 shadow-[0_16px_44px_rgb(26,42,68,0.08)] p-4 space-y-3">
    <div className="flex gap-1 flex-wrap">
      {['Overview', 'Guests', 'Invite', 'Plan', 'Day-of'].map((t, i) => (
        <span key={t} className={`rounded-full px-2.5 py-1 font-body text-[10px] ${i === 0 ? 'bg-gold text-white' : 'bg-ivory text-charcoal/50 border border-gold/15'}`}>{t}</span>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[['Invited', '49'], ['Confirmed', '37'], ['Pending', '8']].map(([l, v]) => (
        <div key={l} className="rounded-xl border border-gold/15 bg-ivory/60 px-2.5 py-2">
          <p className="font-body text-[9px] uppercase tracking-wider text-charcoal/45">{l}</p>
          <p className="font-heading text-lg text-navy leading-tight">{v}</p>
        </div>
      ))}
    </div>
    <div className="space-y-1.5">
      {[['Sent', 'w-[94%]'], ['Opened', 'w-[90%]'], ['RSVPed', 'w-[84%]']].map(([l, w]) => (
        <div key={l} className="space-y-1">
          <p className="font-body text-[9px] text-charcoal/50">{l}</p>
          <div className="h-1 rounded-full bg-gold/12 overflow-hidden"><div className={`h-full ${w} rounded-full bg-gold/70`} /></div>
        </div>
      ))}
    </div>
  </div>
);

const StationInvite = () => (
  <div className="mx-auto w-[210px] rounded-[1.75rem] bg-white border border-gold/25 shadow-[0_16px_44px_rgb(26,42,68,0.10)] p-2.5">
    <div className="rounded-[1.35rem] bg-ivory overflow-hidden">
      <div className="h-24 bg-gradient-to-br from-dusty-rose/35 via-gold/25 to-ivory" />
      <div className="px-3.5 py-3 text-center space-y-2">
        <p className="font-script text-xl text-gold-dark leading-tight">Jun Wei &amp; Mei Ling</p>
        <p className="font-body text-[9px] uppercase tracking-[0.16em] text-charcoal/45">30 August 2026</p>
        <p className="font-body text-[10px] text-charcoal/55 leading-snug">Marina Bay Sands<br />Grand Ballroom</p>
        <div className="rounded-full bg-gradient-gold py-1.5">
          <span className="font-body text-[10px] text-white tracking-wide uppercase">RSVP</span>
        </div>
        <p className="font-body text-[9px] text-charcoal/35">Save the date, or the full invite</p>
      </div>
    </div>
  </div>
);

const StationRsvp = () => (
  <div className="mx-auto w-full max-w-sm rounded-2xl bg-white border border-gold/20 shadow-[0_16px_44px_rgb(26,42,68,0.08)] p-4 space-y-3">
    <div className="grid grid-cols-3 gap-2">
      {[['Invited', '49'], ['Confirmed', '37'], ['Pending', '8']].map(([l, v]) => (
        <div key={l} className="rounded-xl border border-gold/15 bg-ivory/60 px-2.5 py-2">
          <p className="font-body text-[9px] uppercase tracking-wider text-charcoal/45">{l}</p>
          <p className="font-heading text-lg text-navy leading-tight">{v}</p>
        </div>
      ))}
    </div>
    <div className="space-y-1.5">
      {[
        { n: 'Rachel Ng', d: 'Vegetarian', ok: true },
        { n: 'The Sims', d: '3 guests · 1 high chair', ok: true },
        { n: 'Siti Nurhaliza', d: 'Cannot make it', ok: false },
      ].map((r) => (
        <div key={r.n} className="flex items-center justify-between gap-2 rounded-xl border border-gold/12 bg-ivory/60 px-3 py-2">
          <div className="min-w-0">
            <p className="font-body text-[11px] font-semibold text-navy truncate">{r.n}</p>
            <p className="font-body text-[9px] text-charcoal/45 truncate">{r.d}</p>
          </div>
          <Chip tone={r.ok ? 'good' : 'plain'}>{r.ok ? 'Attending' : 'Declined'}</Chip>
        </div>
      ))}
    </div>
    <p className="font-body text-[9px] text-charcoal/40 text-center">Answers land straight in the guest list</p>
  </div>
);

/* The couple's order, not just the banquet: the invite goes out weeks before
 * check-in exists. Mirrors the three stages narrated above the switcher. */
const STATIONS = [
  { key: 'invite',    label: 'The invite',      icon: Mail,       Mock: StationInvite,    path: (id) => `/live/wedding/${id}/invite` },
  { key: 'rsvp',      label: 'RSVPs landing',   icon: Users,      Mock: StationRsvp,      path: (id) => `/plan/wedding/${id}/admin?tab=guests` },
  { key: 'checkin',   label: 'Guest check-in',  icon: Smartphone, Mock: StationCheckin,   path: (id) => `/live/wedding/${id}/checkin` },
  { key: 'reception', label: 'Reception desk',  icon: Tablet,     Mock: StationReception, path: (id) => `/live/wedding/${id}/reception` },
  { key: 'tv',        label: 'Ballroom screen', icon: Monitor,    Mock: StationBallroom,  path: (id) => `/live/wedding/${id}/tv` },
  { key: 'admin',     label: 'Admin console',   icon: Laptop,     Mock: StationAdmin,     path: (id) => `/plan/wedding/${id}/admin` },
];


const Eyebrow = ({ children }) => (
  <p className="font-body text-xs uppercase tracking-[0.22em] text-gold-dark mb-3">{children}</p>
);

/* ── Hero product preview ─────────────────────────────────────────────
   A token-built impression of the real surfaces (invite → check-in →
   arrivals) so the page shows the product instead of only describing it.
   Decorative: hidden from assistive tech, the copy above carries meaning. */
const HeroPreview = () => (
  <div className="relative select-none w-[236px] sm:w-[300px] lg:w-[340px]" aria-hidden="true">
    {/* Invite card peeking out behind the phone. Deliberately wordless — it is
        partly occluded, so any copy here would be clipped mid-word. */}
    <div className="absolute -top-14 right-0 lg:-right-8 w-44 rotate-6 rounded-2xl bg-white/90 backdrop-blur-md border border-gold/20 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-4 hidden sm:block">
      <div className="h-20 rounded-xl bg-gradient-to-br from-dusty-rose/30 to-gold/20" />
      <div className="mt-3 space-y-1.5">
        <div className="h-2 w-3/4 ml-auto rounded-full bg-gold/25" />
        <div className="h-1.5 w-1/2 ml-auto rounded-full bg-charcoal/10" />
      </div>
      <div className="mt-3 h-6 rounded-full bg-gradient-gold-deep" />
    </div>

    {/* Phone: check-in */}
    <div className="relative mx-auto w-[236px] rounded-[2rem] bg-white border border-gold/25 shadow-[0_20px_60px_rgb(26,42,68,0.12)] p-3 z-10">
      <div className="rounded-[1.5rem] bg-ivory p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-body text-[10px] uppercase tracking-[0.18em] text-gold-dark">Check-in</span>
          <QrCode className="w-4 h-4 text-gold-dark" strokeWidth={1.5} />
        </div>
        <div className="rounded-2xl bg-white border border-gold/15 p-3 space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center font-body text-xs font-semibold text-gold-dark">MT</div>
            <div className="min-w-0">
              {/* A guest from the demo seed, so the marketing mock matches what
                  clicking through actually shows. Never a real person's name. */}
              <p className="font-body text-xs font-semibold text-navy truncate">Marcus Teo</p>
              <p className="font-body text-[10px] text-charcoal/50">Groom&rsquo;s side · +1</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full bg-success/10 text-success border border-success/20 px-2 py-0.5 font-body text-[10px]">Arrived</span>
            <span className="rounded-full bg-gold/10 text-gold-dark border border-gold/40 px-2 py-0.5 font-body text-[10px] font-bold tracking-wider uppercase">VIP</span>
            <span className="rounded-full bg-navy/5 text-navy/70 border border-navy/10 px-2 py-0.5 font-body text-[10px]">No beef</span>
          </div>
        </div>
        <div className="rounded-2xl bg-navy p-3 text-center">
          <p className="font-body text-[10px] uppercase tracking-[0.18em] text-gold">Your table</p>
          <p className="font-heading text-2xl text-white leading-tight mt-0.5">8</p>
        </div>
        <div className="h-8 rounded-full bg-gradient-gold-deep flex items-center justify-center gap-1.5">
          <Camera className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
          <span className="font-body text-[10px] uppercase tracking-wider text-white">Snap a selfie</span>
        </div>
      </div>
    </div>

    {/* Arrivals tile in front */}
    <div className="absolute -bottom-8 -left-6 sm:-left-10 lg:-left-14 z-20 w-44 rounded-2xl bg-white/95 backdrop-blur-md border border-gold/20 shadow-[0_12px_40px_rgb(26,42,68,0.10)] p-4">
      <p className="font-body text-[10px] uppercase tracking-[0.18em] text-charcoal/40">Arrived</p>
      <p className="font-heading text-2xl text-navy leading-tight">184<span className="text-base text-charcoal/40"> / 240</span></p>
      <div className="mt-2 h-1.5 rounded-full bg-gold/15 overflow-hidden">
        <div className="h-full w-[77%] rounded-full bg-gradient-gold" />
      </div>
      <p className="font-body text-[10px] text-charcoal/50 mt-2">21 of 24 tables seated</p>
    </div>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [existingId, setExistingId] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinShake, setJoinShake] = useState(false);

  const [demoLoading, setDemoLoading] = useState(false);
  // Which station card started the demo, so only that one shows a spinner.
  const [demoTarget, setDemoTarget] = useState(null);
  const [station, setStation] = useState(STATIONS[0].key);
  // Captured shots are committed assets; a clone without them falls back to
  // the hand-built preview rather than rendering broken images.
  const [heroShotsAvailable, setHeroShotsAvailable] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);

  const [resumeWedding, setResumeWedding] = useState(null); // { id, coupleName } | null
  const creatingRef = useRef(false); // in-flight guard for handleCreate

  /* ── Pro waitlist: email capture while payments are still off.
        Writes a proLeads doc; functions/src/proLead.ts notifies on create. ── */
  const [proOpen, setProOpen] = useState(false);
  const [proEmail, setProEmail] = useState('');
  const [proCoupleName, setProCoupleName] = useState('');
  const [proWeddingDate, setProWeddingDate] = useState('');
  const [proError, setProError] = useState('');
  const [proSubmitting, setProSubmitting] = useState(false);
  const [proLead, setProLead] = useState(null); // { id, email } when submitted

  useEffect(() => {
    try {
      const saved = localStorage.getItem('gw-last-wedding');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.id && /^[a-zA-Z0-9]+$/.test(parsed.id)) {
          setResumeWedding({ id: parsed.id, coupleName: parsed.coupleName || null });
          return;
        }
      }
    } catch { /* ignore corrupt data */ }

    // No saved wedding — try to discover one from Firestore for signed-in users.
    (async () => {
      await auth.authStateReady();
      const u = auth.currentUser;
      if (!u || u.isAnonymous) return;
      try {
        const snap = await getDocs(
          query(
            collection(db, 'weddings'),
            where('ownerUids', 'array-contains', u.uid),
            where('status', '!=', 'purged'),
          )
        );
        for (const d of snap.docs) {
          const data = d.data();
          if (data.isDemo) continue;
          const entry = { id: d.id, coupleName: data.coupleName || null };
          setResumeWedding(entry);
          try { localStorage.setItem('gw-last-wedding', JSON.stringify(entry)); } catch {}
          break;
        }
      } catch { /* permission denied or network — non-fatal */ }
    })();
  }, []);

  const handleCreate = async () => {
    // A ref rather than the createLoading state: setState is async, so two
    // taps in the same tick both read the stale value and both proceed.
    if (creatingRef.current || createLoading) return;
    creatingRef.current = true;
    try {
      // Firebase restores the session asynchronously. Reading currentUser
      // before it settles reports a signed-in couple as signed out.
      await auth.authStateReady();
      const u = auth.currentUser;

      // Signed out: hand off to /start, which resolves the wedding once we know
      // who they are. Deliberately no id minted here — doing that meant a
      // returning couple came back from sign-in holding a new id and was walked
      // through creating a second wedding instead of opening their real one.
      if (!u || u.isAnonymous) {
        navigate('/couple-auth?return=%2Fstart');
        return;
      }

      setCreateLoading(true);
      try {
        const { weddingId, outcome } = await resolveOrCreateWedding(u, {
          resumeHint: resumeWedding?.id || null,
        });
        // Only this button explains itself: the couple asked to create
        // something and got handed an existing wedding instead. /start makes
        // the same journey silently, because there "open my wedding" is the
        // whole intent.
        if (outcome !== 'created') {
          toast.error('You already have a wedding. Opening it now.');
        }
        navigate(`/plan/wedding/${weddingId}/admin`);
      } catch (err) {
        console.error(`Wedding claim: failed [${err?.code || err?.message || 'unknown'}]`, err);
        toast.error(describeClaimError(err));
      } finally {
        setCreateLoading(false);
      }
    } finally {
      creatingRef.current = false;
    }
  };

  /* Seed the demo, then land on whichever page the caller asked for.
   * The demo has to be seeded before any of its routes render anything, so
   * every entry point goes through here rather than linking straight to a
   * /live or /plan URL. `targetKey` drives the per-card spinner. */
  const startDemo = async (buildPath = (id) => `/wedding/${id}/admin`, targetKey = 'admin') => {
    if (demoLoading) return;
    setDemoTarget(targetKey);
    setDemoLoading(true);
    try {
      const resp = await Promise.race([
        fetch('/api/demo/seed', { method: 'POST' }),
        new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 25000)),
      ]);
      if (!resp.ok) {
        let serverMsg = '';
        try { serverMsg = (await resp.json())?.error || ''; } catch { /* non-JSON */ }
        throw new Error(serverMsg || 'seed-failed');
      }
      const { weddingId: id, coupleName } = await resp.json();
      try {
        sessionStorage.setItem(`gw-demo-autounlock-${id}`, '1');
        localStorage.setItem('gw-last-wedding', JSON.stringify({ id, coupleName }));
        ['admin', 'invite', 'reception', 'checkin', 'quiz', 'photos', 'gallery'].forEach((k) => {
          localStorage.removeItem(`gw-demo-${k}-tour-done`);
        });
      } catch { /* storage unavailable — non-fatal */ }
      toast.success('Demo ready!');
      navigate(buildPath(id));
    } catch (err) {
      console.error('Demo seed failed:', err);
      const raw = err?.message || '';
      const msg = raw === 'timeout'
        ? 'Demo is taking too long. Check your connection and try again.'
        : (raw && raw !== 'seed-failed')
          ? `Could not start the demo: ${raw}`
          : 'Could not start the demo. Please try again.';
      toast.error(msg);
      setDemoLoading(false);
      setDemoTarget(null);
    }
  };

  const handleTryDemo = () => startDemo();

  const triggerShake = () => {
    setJoinShake(true);
    setTimeout(() => setJoinShake(false), 500);
  };

  const openProUpgrade = () => {
    setProError('');
    setProLead(null);
    setProOpen(true);
  };

  const handleProSubmit = async (e) => {
    e.preventDefault();
    const email = proEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setProError('Please enter a valid email address.');
      return;
    }
    setProError('');
    setProSubmitting(true);
    const weddingId = generateWeddingId();
    try {
      await setDoc(doc(db, 'proLeads', weddingId), {
        weddingId,
        email,
        coupleName: proCoupleName.trim() || null,
        weddingDate: proWeddingDate || null,
        amountSgd: PRO_PRICE_SGD,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setProLead({ id: weddingId, email });
    } catch (err) {
      setProError('Could not save your details. Please try again or email us.');
      setProSubmitting(false);
      return;
    }
    setProSubmitting(false);
  };

  const handleProContinue = () => {
    if (!proLead) return;
    setProOpen(false);
    navigate(`/wedding/${proLead.id}/admin`);
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    const id = existingId.trim();
    if (!id) { setJoinError('Please enter a wedding ID'); triggerShake(); return; }
    if (id.length < 4 || !/^[a-zA-Z0-9]+$/.test(id)) { setJoinError('Wedding ID must be 4+ alphanumeric characters'); triggerShake(); return; }
    setJoinError('');
    setJoinLoading(true);
    try {
      const snap = await getDoc(doc(db, 'weddings', id));
      if (!snap.exists()) {
        setJoinError('Wedding not found. Please check the ID and try again.');
        triggerShake();
        setJoinLoading(false);
        return;
      }
    } catch (err) {
      const code = err?.code || '';
      if (code === 'permission-denied') {
        await auth.authStateReady();
        const u = auth.currentUser;
        if (!u || u.isAnonymous) {
          const returnPath = `/plan/wedding/${id}/admin`;
          navigate(`/couple-auth?return=${encodeURIComponent(returnPath)}`);
          setJoinLoading(false);
          return;
        }
        setJoinError('You don\'t have access to this wedding. Check the ID or ask the couple to add you.');
        triggerShake();
        setJoinLoading(false);
        return;
      }
    }
    navigate(`/plan/wedding/${id}/admin`);
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="landing-page">

      {/* ─── NAV ─── */}
      <header className="sticky top-0 z-40 bg-ivory/85 backdrop-blur-md border-b border-gold/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
          <a href="#top" className="font-script text-2xl text-gold-dark leading-none">Guest Who</a>
          <nav className="hidden md:flex items-center gap-7" aria-label="Sections">
            {[
              { href: '#how', label: 'How it works' },
              { href: '#features', label: 'Features' },
              { href: '#pricing', label: 'Pricing' },
            ].map((l) => (
              <a key={l.href} href={l.href}
                className="font-body text-sm text-charcoal/60 hover:text-navy transition-colors duration-200">
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* An explicit way back in. Until this existed the only door was
                "Start free", so a returning couple on a device that had lost
                its saved wedding had no option that led to their own data. */}
            <Button
              data-testid="log-in-btn"
              onClick={() => navigate('/couple-auth?return=%2Fstart')}
              variant="ghost"
              className="rounded-full text-charcoal/70 hover:text-navy hover:bg-charcoal/5 transition-colors duration-200 font-medium tracking-wide uppercase text-xs px-4 h-auto min-h-[44px]"
            >
              Log in
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createLoading}
              className="rounded-full bg-gradient-gold-deep text-white shadow-sm hover:shadow-md transition-shadow duration-300 font-medium tracking-wide uppercase text-xs px-5 h-auto min-h-[44px]"
            >
              Start free
            </Button>
          </div>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section id="top" className="px-5 sm:px-6 pt-14 pb-16 md:pt-20 md:pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-16 items-center">

          {/* Left: copy + actions */}
          <div className="max-w-xl w-full mx-auto lg:mx-0 text-center lg:text-left space-y-9">
            <div className="space-y-4 fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-white/70 px-3.5 py-1.5">
                <Sparkles className="w-3.5 h-3.5 text-gold-dark" strokeWidth={1.5} />
                <span className="font-body text-xs text-gold-dark tracking-wide">Built for Singapore banquets</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-navy tracking-tight leading-[1.2]">
                One guest list,<br />
                <span className="text-gold-dark">invite to last table.</span>
              </h1>
              <p className="font-body text-charcoal/70 text-base md:text-lg leading-relaxed">
                Invite, RSVP, seating, check-in — every stage reads from the same
                guest list, so nothing gets re-typed.
              </p>
            </div>

            <div className="space-y-5 fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="create-wedding-btn"
                  onClick={handleCreate}
                  disabled={createLoading}
                  className="flex-1 rounded-full bg-gradient-gold-deep text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-[transform,box-shadow] duration-300 font-medium tracking-wide uppercase text-sm px-8 py-6"
                >
                  {createLoading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" /> Setting Up...
                    </>
                  ) : (
                    <>
                      Create Your Wedding
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>

                <Button
                  data-testid="try-demo-btn"
                  onClick={handleTryDemo}
                  variant="outline"
                  disabled={demoLoading}
                  className="flex-1 rounded-full border-2 border-gold/40 bg-white/60 text-gold-dark hover:bg-gold/10 hover:border-gold transition-colors duration-300 font-medium tracking-wide uppercase text-sm px-8 py-6"
                >
                  {demoLoading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" /> Loading Demo...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="mr-2 w-5 h-5" strokeWidth={1.5} /> Try the Demo
                    </>
                  )}
                </Button>
              </div>

              <p className="font-body text-xs text-charcoal/50">
                The demo opens a fully populated sample wedding &mdash; 240 guests, seating, timeline
                and all. No sign-in, nothing to fill in.
              </p>

              {resumeWedding && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => navigate(`/wedding/${resumeWedding.id}/admin`)}
                    variant="outline"
                    className="flex-1 rounded-full border-2 border-gold/60 bg-white text-gold-dark hover:bg-gold/5 transition-colors duration-300 font-medium tracking-wide uppercase text-sm px-6 py-4"
                  >
                    Resume {resumeWedding.coupleName ? `${resumeWedding.coupleName}'s ` : ''}wedding
                  </Button>
                  <Button
                    onClick={() => {
                      localStorage.removeItem('gw-last-wedding');
                      setResumeWedding(null);
                    }}
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-charcoal/40 hover:text-charcoal/60 hover:bg-charcoal/5 transition-colors"
                    aria-label="Clear resume wedding"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gold/20" />
                <span className="text-sm text-charcoal/40 font-body">or join existing</span>
                <div className="h-px flex-1 bg-gold/20" />
              </div>

              <form onSubmit={handleJoin} className="space-y-2">
                <div className={`flex gap-2 sm:gap-3 ${joinShake ? 'animate-shake' : ''}`}>
                  <Input
                    data-testid="existing-wedding-id-input"
                    value={existingId}
                    onChange={(e) => { setExistingId(e.target.value); setJoinError(''); }}
                    placeholder="Enter wedding ID"
                    className={`rounded-full bg-white border px-4 sm:px-6 h-auto min-h-[48px] text-base font-body ${joinError ? 'border-red-400' : 'border-gold/30'}`}
                  />
                  <Button
                    data-testid="join-wedding-btn"
                    type="submit"
                    variant="outline"
                    disabled={joinLoading}
                    className="rounded-full border-2 border-gold text-gold-dark hover:bg-gold/10 transition-colors duration-300 px-5 sm:px-6 h-auto min-h-[48px]"
                  >
                    {joinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Go'}
                  </Button>
                </div>
                {joinError && <p className="font-body text-sm text-red-500 text-center" aria-live="polite">{joinError}</p>}
              </form>

              <p className="font-body text-xs text-charcoal/40">
                Helping with a wedding, or got an invite? Just open the link they sent you &mdash;
                guests never sign in.
              </p>
            </div>
          </div>

          {/* Right: the real product, or the hand-built mock until it exists */}
          <div className="fade-up pt-4 pb-14 lg:pb-0" style={{ animationDelay: '0.3s' }}>
            {heroShotsAvailable
              ? <HeroShots onUnavailable={() => setHeroShotsAvailable(false)} />
              : <div className="flex justify-center lg:justify-end"><HeroPreview /></div>}
          </div>
        </div>
      </section>

      {/* ─── TRUST STRIP ─── */}
      <section className="bg-white border-y border-gold/10 px-5 sm:px-6 py-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4">
          {[
            { icon: Smartphone, label: 'Runs in the browser', sub: 'Guests install nothing' },
            { icon: Users, label: '200–500 guests', sub: 'Banquet-scale tested' },
            { icon: Wallet, label: 'Free in early access', sub: 'Every feature, no cap' },
            { icon: Heart, label: 'Made in Singapore', sub: 'SG venues and customs' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-2.5">
              <item.icon className="w-4 h-4 text-gold-dark flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0">
                <p className="font-body text-sm text-navy font-medium leading-snug">{item.label}</p>
                <p className="font-body text-xs text-charcoal/50 leading-snug">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how" className="bg-ivory px-5 sm:px-6 py-16 md:py-24 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <FadeSection className="max-w-2xl mb-12">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="font-heading text-2xl md:text-4xl text-navy tracking-tight">
              Three stages, one guest list
            </h2>
            <p className="font-body text-charcoal/60 mt-3 leading-relaxed">
              Let guests do the data entry for you. Every screen below reads from the same list.
            </p>
          </FadeSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 lg:gap-x-12 gap-y-9">
            {[
              {
                step: '01',
                title: 'Build the invite',
                text: 'Pick a theme, add your photos and story. Already have an Excel rundown? Upload it and the timeline fills itself in.',
              },
              {
                step: '02',
                title: 'Guests RSVP themselves',
                text: 'Share one WhatsApp link. Answers land straight in your guest list — dietary needs, plus-ones, children and all.',
              },
              {
                step: '03',
                title: 'Run the day',
                text: 'The same list becomes check-in. Guests scan, find their table, and you watch arrivals fill up from your phone.',
              },
            ].map((item, i) => (
              <FadeSection key={item.step} delay={i * 120}>
                {/* Ruled columns, not cards. These three steps are 83 words
                    between them, but a bordered box around each made them read
                    as three things to get through. Dropping the chrome cuts more
                    perceived weight than cutting words would, and the numerals
                    carry the sequence on their own. */}
                <div className="border-t border-gold/30 pt-5">
                  <p className="font-body text-xs tracking-[0.22em] text-gold-dark mb-4">{item.step}</p>
                  <h3 className="font-heading text-lg text-navy font-semibold mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-charcoal/65 leading-relaxed">{item.text}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SEE IT LIVE ───
          Was four cards each describing a page in a paragraph. Now one frame
          the tabs swap, with every tab a way into the running demo — the
          reader can go look instead of reading about it. */}
      <section id="see-it" className="bg-ivory px-5 sm:px-6 py-16 md:py-20 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <FadeSection className="text-center max-w-xl mx-auto mb-8">
            <Eyebrow>See it working</Eyebrow>
            <h2 className="font-heading text-2xl md:text-4xl text-navy tracking-tight">
              Every screen, one guest list
            </h2>
            <p className="font-body text-charcoal/60 mt-3 leading-relaxed">
              Open any of them in the live demo.
            </p>
          </FadeSection>

          <FadeSection>
            {/* Tabs */}
            {/* Horizontal scroll rather than wrap: six tabs stack to three rows
                at 390px. Mirrors DayOfTab's own sub-tab nav so the marketing
                page and the product behave the same way. */}
            <div
              role="tablist"
              aria-label="Screens across the wedding"
              className="overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 pb-1 mb-8"
            >
              <div className="flex gap-2 w-max min-w-full sm:justify-center">
              {STATIONS.map((st) => {
                const active = st.key === station;
                return (
                  <button
                    key={st.key}
                    role="tab"
                    type="button"
                    aria-selected={active}
                    aria-controls="station-panel"
                    onClick={() => setStation(st.key)}
                    data-testid={`station-tab-${st.key}`}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-4 py-2 font-body text-sm whitespace-nowrap transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 ${
                      active
                        ? 'bg-navy text-white'
                        : 'bg-white border border-gold/25 text-charcoal/60 hover:border-gold/50 hover:text-charcoal'
                    }`}
                  >
                    <st.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {st.label}
                  </button>
                );
              })}
              </div>
            </div>

            {/* The one frame every tab swaps */}
            <div
              id="station-panel"
              role="tabpanel"
              /* Sized to the mocks inside it (210–384px), not to the page
                 container — at max-w-5xl the frame read as mostly empty. */
              className="mx-auto w-full max-w-xl rounded-3xl border border-gold/15 bg-white/60 backdrop-blur-sm px-5 py-8 flex items-center justify-center min-h-[300px]"
            >
              {(() => {
                const { Mock } = STATIONS.find((st) => st.key === station);
                return <Mock />;
              })()}
            </div>

            {/* Straight into whichever station is showing */}
            <div className="flex justify-center mt-6">
              <Button
                onClick={() => {
                  const st = STATIONS.find((x) => x.key === station);
                  startDemo(st.path, st.key);
                }}
                disabled={demoLoading}
                data-testid="station-open-demo"
                className="rounded-full bg-gradient-gold text-white font-medium tracking-wide uppercase text-sm px-8 py-6"
              >
                {demoLoading && demoTarget === station ? (
                  <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Opening&hellip;</>
                ) : (
                  <>Open this in the demo <ArrowRight className="ml-2 w-4 h-4" /></>
                )}
              </Button>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" className="bg-white px-5 sm:px-6 py-16 md:py-24 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <FadeSection className="max-w-2xl mb-12">
            <Eyebrow>What&rsquo;s inside</Eyebrow>
            <h2 className="font-heading text-2xl md:text-4xl text-navy tracking-tight">
              Everything the people side needs
            </h2>
            <p className="font-body text-charcoal/60 mt-3 leading-relaxed">
              Not a to-do list app with a wedding skin. Each piece knows what the others know.
            </p>
          </FadeSection>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Wide: the invite, still the thing couples judge first */}
            <FadeSection className="md:col-span-4">
              <div className="h-full rounded-2xl bg-ivory border border-gold/15 p-6 md:p-8">
                <div className="w-11 h-11 rounded-2xl bg-gold/10 flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-gold-dark" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg text-navy font-semibold mb-2">A digital invite that looks made for you</h3>
                <p className="font-body text-sm text-charcoal/65 leading-relaxed mb-4">
                  Seven themes, your story and photos, live preview as you edit.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['7 themes', 'Our Story', 'Food photos', 'Background music', 'Countdown', 'Bilingual'].map((t) => (
                    <span key={t} className="rounded-full border border-gold/25 bg-white px-3 py-1 font-body text-xs text-charcoal/60">{t}</span>
                  ))}
                </div>
              </div>
            </FadeSection>

            <FadeSection delay={80} className="md:col-span-2">
              <div className="h-full rounded-2xl bg-ivory border border-gold/15 p-6 md:p-7">
                <div className="w-11 h-11 rounded-2xl bg-gold/10 flex items-center justify-center mb-4">
                  <Users className="w-5 h-5 text-gold-dark" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg text-navy font-semibold mb-2">Group RSVP</h3>
                <p className="font-body text-sm text-charcoal/65 leading-relaxed">
                  One booking code per family. Everyone answers for themselves.
                </p>
              </div>
            </FadeSection>

            {[
              {
                icon: LayoutGrid,
                title: 'Seating that bends',
                text: 'Per-person tables, live capacity, and your floor plan at the door.',
              },
              {
                icon: CalendarClock,
                title: 'Plan the whole run',
                text: 'Checklist, tasks, vendors, cars and a day-of timeline split by side.',
              },
              {
                icon: Wallet,
                title: 'Budget and ang bao',
                text: 'Vendor contracts, payments as you make them, ang bao in and net position.',
              },
              {
                icon: Heart,
                title: 'Built for SG banquets',
                text: 'Round tables, Chinese, Malay and Indian variants, halal and no-beef as standard.',
              },
            ].map((item, i) => (
              <FadeSection key={item.title} delay={(i % 3) * 80} className="md:col-span-3">
                <div className="h-full rounded-2xl bg-ivory border border-gold/15 p-6 hover:border-gold/35 transition-colors duration-300">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center mb-3.5">
                    <item.icon className="w-5 h-5 text-gold-dark" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-base text-navy font-semibold mb-1.5">{item.title}</h3>
                  <p className="font-body text-sm text-charcoal/65 leading-relaxed">{item.text}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" className="bg-ivory px-5 sm:px-6 py-16 md:py-24 scroll-mt-16">
        <div className="max-w-4xl mx-auto">
          <FadeSection className="text-center mb-10">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="font-heading text-2xl md:text-4xl text-navy tracking-tight">
              Free while we&rsquo;re in early access
            </h2>
            <p className="font-body text-charcoal/60 mt-3 max-w-xl mx-auto">
              Every feature unlocked, no guest cap, no card.
            </p>
          </FadeSection>

          {/* One card, not two. The two-card version listed the free tier's
              six real features beside four *reassurances* on the paid one —
              "the feature list does not change", "we will email you" — so the
              paid tier read as the same product with extra caveats. Nobody
              wants that tier.

              The price now anchors instead of competing: S$59 struck through,
              Free beside it. Struck-through prices normally mean "was", and
              S$59 has never been charged — so it is labelled as the PLANNED
              price everywhere it appears. Anchoring against a price nobody
              ever paid, without saying so, would be a fake discount. */}
          <FadeSection delay={100}>
            <Card className="rounded-2xl border-2 border-gold bg-white shadow-xl max-w-xl mx-auto">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-end justify-center gap-3">
                  <span className="font-heading text-2xl text-charcoal/35 line-through decoration-charcoal/30 decoration-2 leading-none pb-1.5">
                    S${PRO_PRICE_SGD}
                  </span>
                  <span className="font-heading text-5xl md:text-6xl text-navy font-bold leading-none">Free</span>
                </div>
                <p className="font-body text-sm text-charcoal/55 text-center mt-3">
                  S${PRO_PRICE_SGD} one-time is the <span className="text-charcoal/75">planned</span> price.
                  Payments are not switched on, so nobody has paid it yet.
                </p>

                <div className="mt-6 pt-6 border-t border-gold/15 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {[
                    'Digital invite, all 7 themes, Our Story and music',
                    'RSVP links, group booking codes, dietary and transport',
                    'Guest list, per-person seating and health checks',
                    'Checklist, timeline, vendors, budget and ang bao',
                    'QR check-in, ballroom TV, lucky draw and quiz',
                    'Coordinator seats and CSV export',
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-gold-dark flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <p className="font-body text-sm text-charcoal/70 leading-relaxed">{feature}</p>
                    </div>
                  ))}
                </div>

                <Button
                  data-testid="free-start-btn"
                  onClick={handleCreate}
                  disabled={createLoading}
                  className="mt-7 w-full rounded-full bg-gradient-gold-deep text-white shadow-lg hover:shadow-xl transition-shadow duration-300 font-medium tracking-wide uppercase py-5 text-sm"
                >
                  Create Your Wedding <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <AgreeNotice action="creating your wedding" className="mt-3" />

                <p className="font-body text-xs text-charcoal/50 text-center mt-4 leading-relaxed">
                  Never a subscription, no per-guest fee. Couples already using Guest Who keep
                  what they have, and we&rsquo;ll email well before anything changes.{' '}
                  <button
                    data-testid="pro-upgrade-btn"
                    onClick={openProUpgrade}
                    className="text-gold-dark underline underline-offset-2 hover:text-gold"
                  >
                    Tell me when it launches
                  </button>
                </p>
              </CardContent>
            </Card>
          </FadeSection>
        </div>
      </section>

      {/* ─── FAQ ───
          Was six question-and-answer blocks all open at once — 233 words and
          the tallest thing on the page for content most readers skip. Now a
          collapsed accordion: six one-line rows, with the "email us" escape
          hatch moved into the left column instead of trailing underneath.

          The first row starts open so the rows read as expandable rather than
          as a list of links (the Mixpanel pattern). `collapsible` lets it
          close again, and `type="single"` keeps only one answer open, so the
          section never grows back to its old height. */}
      <section className="bg-white px-5 sm:px-6 py-16 md:py-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[0.7fr_1.3fr] gap-8 lg:gap-14">
          <FadeSection>
            <Eyebrow>Before you start</Eyebrow>
            <h2 className="font-heading text-2xl md:text-4xl text-navy tracking-tight">Straight answers</h2>
            <a href="mailto:hello@guest-who.com"
              className="inline-flex items-center gap-2 mt-4 font-body text-sm text-charcoal/50 hover:text-gold-dark transition-colors duration-200">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Still wondering something? Email us
            </a>
          </FadeSection>

          <FadeSection delay={80}>
            <Accordion type="single" collapsible defaultValue="faq-0" className="border-t border-gold/15">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border-b border-gold/15">
                  <AccordionTrigger className="py-4 text-left font-heading text-base text-navy font-semibold hover:no-underline hover:text-gold-dark [&>svg]:text-gold-dark">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 font-body text-sm text-charcoal/65 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeSection>
        </div>
      </section>

      {/* ─── CLOSING CTA ───
          The standalone emotional-hook section folded in here: both were doing
          the same job at the end of the page, one after the other. */}
      <section className="bg-navy px-5 sm:px-6 py-16 md:py-24">
        <FadeSection className="max-w-2xl mx-auto text-center space-y-7">
          <Music className="w-6 h-6 text-gold mx-auto" strokeWidth={1.5} />
          <p className="font-heading text-xl md:text-2xl text-white/90 leading-relaxed">
            The table numbers sort themselves out at the door, and you get to actually be
            at your own wedding.
          </p>
          <h2 className="font-script text-3xl md:text-4xl text-gold">
            Start with the guest list. The rest follows.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleCreate}
              disabled={createLoading}
              className="rounded-full bg-gradient-gold-deep text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-[transform,box-shadow] duration-300 font-medium tracking-wide uppercase text-sm px-8 py-6"
            >
              Create Your Wedding <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={handleTryDemo}
              variant="outline"
              disabled={demoLoading}
              className="rounded-full border-2 border-gold/50 bg-transparent text-gold hover:bg-gold/15 hover:border-gold transition-colors duration-300 font-medium tracking-wide uppercase text-sm px-8 py-6"
            >
              <PlayCircle className="mr-2 w-5 h-5" strokeWidth={1.5} /> Try the Demo
            </Button>
          </div>
        </FadeSection>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-navy px-5 sm:px-6 py-12">
        <div className="max-w-4xl mx-auto text-center space-y-2">
          <p className="font-script text-2xl text-gold">Guest Who</p>
          <p className="font-body text-sm text-white/60">&copy; {new Date().getFullYear()} Guest Who</p>
          <p className="font-body text-sm text-white/50">
            Made with <Heart className="w-3 h-3 inline fill-dusty-rose text-dusty-rose" /> in Singapore
          </p>
          <div className="flex items-center justify-center gap-4 pt-2">
            <a href="/privacy.html" className="font-body text-sm text-white/60 hover:text-gold transition-colors duration-200">Privacy Policy</a>
            <span className="text-white/30">·</span>
            <a href="/terms.html" className="font-body text-sm text-white/60 hover:text-gold transition-colors duration-200">Terms of Service</a>
          </div>
        </div>
      </footer>

      {/* ─── PRO UPGRADE DIALOG ─── */}
      <Dialog open={proOpen} onOpenChange={(open) => {
        setProOpen(open);
        if (!open) {
          setProError('');
          setProSubmitting(false);
        }
      }}>
        <DialogContent data-testid="pro-upgrade-dialog" className="bg-ivory border-gold/20 rounded-2xl max-w-md">
          {!proLead ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-navy text-center">
                  Tell me when Pro launches
                </DialogTitle>
                <DialogDescription className="font-body text-charcoal/60 text-center pt-1">
                  Planned at S${PRO_PRICE_SGD} one-time per wedding
                </DialogDescription>
              </DialogHeader>

              <div className="bg-white/60 rounded-xl p-4 border border-gold/10 text-center">
                <p className="font-body text-sm text-charcoal/70 leading-relaxed">
                  Payments aren&rsquo;t switched on yet and everything is free in the meantime.
                  Leave your email and we&rsquo;ll let you know before that changes &mdash; there&rsquo;s
                  nothing to pay today.
                </p>
              </div>

              {/* noValidate: the browser's own bubble for type="email" fired
                  before handleProSubmit could run, so the styled inline error
                  below was unreachable. Our EMAIL_RE check is the validator. */}
              <form onSubmit={handleProSubmit} className="space-y-3" noValidate>
                <div>
                  <label className="font-body text-sm text-charcoal/70 block mb-1.5">Email <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="pro-email-input"
                    type="email"
                    required
                    value={proEmail}
                    onChange={(e) => { setProEmail(e.target.value); setProError(''); }}
                    placeholder="you@example.com"
                    className="rounded-full bg-white border border-gold/30 px-5 py-5 text-base font-body"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-charcoal/70 block mb-1.5">Couple names <span className="text-charcoal/40">(optional)</span></label>
                  <Input
                    data-testid="pro-couple-input"
                    value={proCoupleName}
                    onChange={(e) => setProCoupleName(e.target.value)}
                    placeholder="Alex & Jamie"
                    className="rounded-full bg-white border border-gold/30 px-5 py-5 text-base font-body"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-charcoal/70 block mb-1.5">Wedding date <span className="text-charcoal/40">(optional)</span></label>
                  <Input
                    data-testid="pro-date-input"
                    type="date"
                    value={proWeddingDate}
                    onChange={(e) => setProWeddingDate(e.target.value)}
                    className="rounded-full bg-white border border-gold/30 px-5 py-5 text-base font-body"
                  />
                </div>

                {proError && (
                  <p className="font-body text-sm text-red-500 text-center" aria-live="polite" data-testid="pro-error">
                    {proError}
                  </p>
                )}

                <Button
                  data-testid="pro-submit-btn"
                  type="submit"
                  disabled={proSubmitting}
                  className="w-full rounded-full bg-gradient-gold-deep text-white shadow-lg hover:shadow-xl transition-shadow duration-300 font-medium tracking-wide uppercase py-5 text-sm"
                >
                  {proSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Keep Me Posted <ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>

                <p className="font-body text-xs text-charcoal/50 text-center">
                  No card, no charge, no commitment. Just a heads-up when pricing starts.
                </p>
              </form>
            </>
          ) : (
            <div data-testid="pro-confirmation" className="space-y-5 text-center py-2">
              <div className="w-14 h-14 rounded-full bg-gold/15 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-gold-dark" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-navy text-center">
                  We&rsquo;ll keep you posted
                </DialogTitle>
                <DialogDescription className="font-body text-charcoal/60 text-center pt-1">
                  We&rsquo;ve got <span className="text-navy font-medium">{proLead.email}</span> and will
                  email before pricing starts.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-white/60 rounded-xl p-4 border border-gold/10">
                <p className="font-body text-sm text-charcoal/70 leading-relaxed">
                  Now go set up your wedding. Every feature is unlocked and there is nothing to pay.
                </p>
              </div>
              <Button
                data-testid="pro-continue-btn"
                onClick={handleProContinue}
                className="w-full rounded-full bg-gradient-gold-deep text-white shadow-lg hover:shadow-xl transition-shadow duration-300 font-medium tracking-wide uppercase py-5 text-sm"
              >
                Continue to Dashboard <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
